'use client';

import { useState, type CSSProperties, type JSX, type SyntheticEvent } from 'react';

import Image from 'next/image';

import Palette from 'DesignSystem2/Theme/Palette';
import { proxyCdnImageUrl, toCdnImageUrl } from 'utils/images';

import type { ImageFetchPriority, ImageLoading } from './types';

interface SquareImageProps {
    src: string;
    width?: number;
    height?: number;
    alt: string;
    className?: string;
    style?: CSSProperties;
    enforceBypassNextImage?: boolean;
    mirror?: boolean;
    autoWidth?: boolean; // Optional prop to allow auto width
    onError?: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
    onLoad?: () => void;
    onClick?: () => void;
    showLoadingBackground?: boolean;
    quality?: number;
    loading?: ImageLoading;
    fetchPriority?: ImageFetchPriority;
    /** Overrides the default `sizes` attribute (`100vw` when `width` is unset). */
    sizes?: string;
    /**
     * Marks the image as high priority (LCP candidate): eager fetchpriority=high
     * load, and next/image preloads it from the initial HTML when server-rendered.
     * Supersedes `loading`/`fetchPriority`.
     */
    priority?: boolean;
    collapseOnError?: boolean;
    /**
     * Renders at 100% of the parent's height instead of the `height` prop's pixel value.
     * `height` still supplies the intrinsic width/height attributes; only the rendered box
     * size changes. For a parent whose height isn't known ahead of render.
     */
    fillHeight?: boolean;
}

const isValidImageSrc = (src: string): boolean =>
    src.startsWith('/') || src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:image/');

const OptimizedImage = ({
    src,
    width,
    height,
    alt,
    className,
    style,
    enforceBypassNextImage = false,
    mirror,
    autoWidth,
    onError,
    onClick,
    onLoad,
    showLoadingBackground = false,
    quality = 80,
    loading = 'lazy',
    fetchPriority = 'auto',
    sizes,
    priority = false,
    collapseOnError = false,
    fillHeight = false,
}: SquareImageProps): JSX.Element | null => {
    const [failedSrc, setFailedSrc] = useState<string | null>(null);
    /*
     * Use regular img tag to overcome the issue with next/image not supporting cache invalidation
     * The image sources array should be cleaned up when cache is gone
     * This is a temporary solution until the issue is resolved
     * Ex: const bypassNextImageSources: string[] = ['OPENAICEOCHANGE.webp', 'APPLEAI-24-ANTH.webp']
     */
    // const bypassNextImageSources: string[] = ['series-images-webp', 'market-images']; // To bypass all series and market images after a batch change.
    // Matched case-insensitively as a substring of the resolved src, so a ticker
    // prefix bypasses every image whose filename starts with it (all sizes).
    const bypassNextImageSources: string[] = [
        'kxliuk',
        'kxliusa',
        'kxloveisland',
        'kxligamxgame',
        // Bundesliga and Bundesliga 2 series artwork (all market types).
        'kxbundesliga',
        'kxpll',
        'kxivonescimab',
        // Country/team/player flags and logos: a high-cardinality, already-optimized-at-source
        // CDN webp set. Routing them through next/image's on-demand transformer multiplies each
        // file into several distinct (width x quality) cache keys — under a burst of concurrent
        // cold-cache requests (e.g. a sports listing page rendering many fixtures at once), this
        // can queue for 10s+ per image. Serving the source webp directly avoids the transform.
        'structured_targets',
        // ATP tennis artwork refresh (2026-08)
        'kxatp.webp',
        'kxatpmatch.webp',
        'kxatpchallengermatch.webp',
        'kxatpdoubles.webp',
        'kxatpexactmatch.webp',
        'kxatpsetwinner.webp',
        'kxatpgspread.webp',
        'kxatpgtotal.webp',
        'kxatpgrandslam.webp',
        'kxatpfinalsqual.webp',
        'kxatp1rank.webp',
        'kxatpreturn-27',
        'kxplayerdeal.webp',
        'kxatpretire.webp',
    ]; // Bypass matching series/market images after an artwork change.
    // Route browser-bound CloudFront image fetches through a same-origin
    // `/cdn-images/...` proxy (configured in `next.config.js`) so they pick
    // up the `Cache-Control: max-age=3600, stale-while-revalidate=86400`
    // header we attach there. The browser serves repeats from disk/memory
    // cache immediately (no reload blink) while revalidating in the
    // background, so updated artwork at the same ticker path still
    // propagates within a day.
    const resolvedSrc = proxyCdnImageUrl(toCdnImageUrl(src));
    const shouldRenderImage = isValidImageSrc(resolvedSrc);
    const hasFailed = failedSrc === resolvedSrc;
    if (collapseOnError && (!shouldRenderImage || hasFailed)) return null;

    const lowerResolvedSrc = resolvedSrc.toLowerCase();
    const bypassNextImage =
        bypassNextImageSources.some((source) => lowerResolvedSrc.includes(source.toLowerCase())) ||
        enforceBypassNextImage;
    const widthStyle = !width ? (autoWidth ? 'auto' : '100%') : width;
    const heightStyle = fillHeight ? '100%' : !height ? 'auto' : height;

    const commonStyles = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: widthStyle,
        minWidth: widthStyle,
        height: heightStyle,
        borderRadius: style?.borderRadius || '8px',
    };

    const classnames = mirror || className ? `${mirror ? 'scale-x-[-1] ' : ''}${className}` : '';
    const handleError = (event: SyntheticEvent<HTMLImageElement, Event>): void => {
        // Keyed by src so a caller that swaps in a replacement src on error
        // (e.g. TradeSlipCard rotating image candidates) gets a fresh attempt.
        setFailedSrc(resolvedSrc);
        onError?.(event);
    };

    return (
        <div
            style={{
                // A failed load keeps the wrapper as a neutral tile (the app's
                // standard empty-image treatment) rather than rendering the
                // browser's broken-image glyph or retrying the dead URL.
                background: hasFailed ? Palette.Fill.x60 : showLoadingBackground ? Palette.Fill.x50 : 'transparent',
                ...commonStyles,
            }}
            onClick={onClick}
        >
            {shouldRenderImage && !hasFailed && (
                <>
                    {bypassNextImage ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            width={2 * (width || 1)} // Double the size to ensure the image is sharp on retina displays
                            height={2 * (height || 1)}
                            src={resolvedSrc}
                            style={{
                                objectFit: 'cover',
                                ...commonStyles,
                                ...style,
                            }}
                            className={classnames}
                            sizes={sizes ?? (!width ? '100vw' : undefined)}
                            alt={alt}
                            loading={priority ? 'eager' : loading}
                            fetchPriority={priority ? 'high' : fetchPriority}
                            onLoad={() => {
                                if (onLoad) onLoad();
                            }}
                            onErrorCapture={handleError}
                        />
                    ) : (
                        <Image
                            width={2 * (width || 1)} // Double the size to ensure the image is sharp on retina displays
                            height={2 * (height || 1)}
                            quality={quality}
                            src={resolvedSrc}
                            style={{
                                width: widthStyle,
                                height: heightStyle,
                                borderRadius: style?.borderRadius || '8px',
                                objectFit: 'cover',
                                ...style,
                            }}
                            className={classnames}
                            sizes={sizes ?? (!width ? '100vw' : undefined)}
                            alt={alt}
                            onLoad={() => {
                                if (onLoad) onLoad();
                            }}
                            onErrorCapture={handleError}
                            priority={priority}
                            // next/image's `priority` disables lazy loading and emits the
                            // preload, but does NOT set fetchpriority=high on either the img
                            // or the preload (verified against next 16.2.9 get-img-props /
                            // ImagePreload) — pass it explicitly so both carry it, or the
                            // Lighthouse "LCP request discovery" check fails.
                            fetchPriority={priority ? 'high' : fetchPriority}
                            // next/image throws on `priority` + `loading="lazy"`.
                            loading={priority ? undefined : loading}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default OptimizedImage;
